import {
  ApiError,
  getStorage,
  uploadPhoto,
  type PhotoSummary,
  type StorageState,
} from "../api";
import { createMap } from "../map";
import { icon } from "../icons";
import { escapeHtml, formatBytes, isHeic, toDatetimeLocal } from "../format";
import { uploadErrorMessage, uploadToSummary } from "../upload-view";
import {
  applyKept,
  canUpload,
  createQueueItem,
  keptValues,
  MAX_BATCH,
  NO_KEEP,
  remainingSlots,
  summarize,
  uploadFieldsFor,
  type KeepFlags,
  type QueueItem,
} from "../upload-queue";
import { enableSwipeToClose } from "./sheet";
import { categorySelectMarkup, locationPickerMarkup } from "./markup";
import { wireCategorySelect } from "./select";
import { createLocationPicker, type LocationPicker } from "./location-picker";

const qs = <T extends Element>(sel: string) => document.querySelector<T>(sel)!;

/** Dipendenze dal contesto di `renderApp` (mappa, private mode, refresh UI). */
export interface UploadDeps {
  map: ReturnType<typeof createMap>;
  isPrivateModeActive: () => boolean;
  updateScrim: () => void;
  render: () => void;
  refresh: () => Promise<void>;
  setStatus: (text: string | null) => void;
  prependPhoto: (photo: PhotoSummary) => void;
}

export interface UploadController {
  open(): Promise<void>;
  close(): void;
  isOpen(): boolean;
}

/** Upload (coda multi-foto, Batch A) — controller estratto in modulo dedicato
 * (U1b, segue il seam preparato in U1a). Comportamento invariato. */
export function mountUpload(deps: UploadDeps): UploadController {
  let uploadLocationPicker: LocationPicker | null = null;
  // Cleanup degli objectURL delle thumbnail della coda corrente (impostato in
  // openUpload, eseguito alla chiusura per non lasciare memoria appesa).
  let uploadCleanup: (() => void) | null = null;

  function closeUpload(): void {
    const panel = qs<HTMLDivElement>("#upload");
    panel.hidden = true;
    panel.innerHTML = "";
    deps.updateScrim();
    if (uploadLocationPicker) {
      uploadLocationPicker.destroy();
      uploadLocationPicker = null;
    }
    if (uploadCleanup) {
      uploadCleanup();
      uploadCleanup = null;
    }
  }

  async function openUpload(): Promise<void> {
    const panel = qs<HTMLDivElement>("#upload");
    let maxBytes = 0;
    let storageWarn = "";
    try {
      const s = await getStorage();
      maxBytes = s.maxPhotoBytes;
      if (s.state === "full") storageWarn = "Spazio esaurito: upload non possibile.";
      else if (s.state === "warning") storageWarn = "Attenzione: spazio quasi esaurito.";
    } catch {
      /* limiti non disponibili: si affida alla validazione backend */
    }

    // Layout upload FULLSCREEN: header + rail + footer fissi, un solo scroll
    // principale (.up-scroll) per l'editor. Il tap-to-pin sulla mappa è sospeso
    // (lo sheet copre la mappa): la posizione arriva da EXIF / ricerca / "Usa la
    // posizione attuale". Vedi #upload.sheet in components.css.
    const privacyKeepRow = deps.isPrivateModeActive()
      ? `<label class="switch-row up-keep__row">
            <span class="switch-row__label">Stessa privacy per tutte</span>
            <span class="switch">
              <input id="upKeepPriv" class="switch__input" type="checkbox" role="switch" aria-label="Stessa privacy per tutte le foto" />
              <span class="switch__thumb"></span>
            </span>
          </label>`
      : "";
    const privacyEditorRow = deps.isPrivateModeActive()
      ? `<label class="switch-row">
            <span class="switch-row__label">Privata</span>
            <span class="switch">
              <input id="upPrivate" class="switch__input" type="checkbox" role="switch" aria-label="Privata" />
              <span class="switch__thumb"></span>
            </span>
          </label>
          <p class="form__hint">I tag genere (privati) si impostano dal dettaglio di ogni foto dopo il caricamento.</p>`
      : "";

    panel.innerHTML = `
      <div class="sheet__top">
        <div class="sheet__handle"></div>
        <div class="sheet__head">
          <strong class="sheet__title">Nuove foto</strong>
          <button type="button" id="upClose" class="icon-btn" title="Chiudi" aria-label="Chiudi">${icon("close", { label: "Chiudi" })}</button>
        </div>
      </div>
      <form id="upForm" class="form up-form">
        ${storageWarn ? `<p class="form__warn">${escapeHtml(storageWarn)}</p>` : ""}
        <div class="file-field">
          <input id="upFile" type="file" accept="image/jpeg,image/png,image/webp" multiple />
          <p class="form__hint">Puoi scegliere fino a ${MAX_BATCH} foto per volta.</p>
        </div>

        <div id="upQueue" class="up-rail" role="listbox" aria-label="Foto in coda" aria-live="polite"></div>

        <div class="up-scroll">
          <div id="upActive" class="up-active" hidden>
            <p class="form__hint">Stai modificando <strong id="upActiveName"></strong></p>
            ${locationPickerMarkup(false)}
            <label class="field">
              <span class="field__label">Data scatto</span>
              <input id="upDate" class="field__input" type="datetime-local" />
            </label>
            <label class="field">
              <span class="field__label">Descrizione</span>
              <textarea id="upDesc" class="field__control" rows="2"></textarea>
            </label>
            <div class="field">
              <span class="field__label">Categorie</span>
              ${categorySelectMarkup("upCats", [])}
            </div>
            <label class="field">
              <span class="field__label">Nome luogo</span>
              <input id="upPlace" class="field__input" type="text" maxlength="120" />
              <span class="field__hint">Etichetta del luogo salvata con la foto.</span>
            </label>
            ${privacyEditorRow}
          </div>

          <details class="up-keep">
            <summary class="up-keep__summary">Mantieni per tutte le foto</summary>
            <p class="form__hint">Posizione e data restano sempre per-foto.</p>
            <label class="switch-row up-keep__row">
              <span class="switch-row__label">Stesse categorie per tutte</span>
              <span class="switch">
                <input id="upKeepCats" class="switch__input" type="checkbox" role="switch" aria-label="Stesse categorie per tutte le foto" />
                <span class="switch__thumb"></span>
              </span>
            </label>
            <label class="switch-row up-keep__row">
              <span class="switch-row__label">Stesso nome luogo per tutte</span>
              <span class="switch">
                <input id="upKeepPlace" class="switch__input" type="checkbox" role="switch" aria-label="Stesso nome luogo per tutte le foto" />
                <span class="switch__thumb"></span>
              </span>
            </label>
            <label class="switch-row up-keep__row">
              <span class="switch-row__label">Stessa descrizione per tutte</span>
              <span class="switch">
                <input id="upKeepDesc" class="switch__input" type="checkbox" role="switch" aria-label="Stessa descrizione per tutte le foto" />
                <span class="switch__thumb"></span>
              </span>
            </label>
            ${privacyKeepRow}
          </details>
        </div>

        <div class="up-footer">
          <div class="up-footer__info">
            <p id="upSummary" class="up-footer__summary" aria-live="polite"></p>
            <p id="upMsg" class="form__msg" aria-live="polite"></p>
          </div>
          <button type="submit" id="upSubmit" class="btn" disabled>${icon("add", { size: 18 })} Carica</button>
        </div>
      </form>`;
    panel.hidden = false;
    deps.updateScrim();
    enableSwipeToClose(panel, closeUpload);

    const fileInput = qs<HTMLInputElement>("#upFile");
    const dateInput = qs<HTMLInputElement>("#upDate");
    const descInput = qs<HTMLTextAreaElement>("#upDesc");
    const placeInput = qs<HTMLInputElement>("#upPlace");
    const msg = qs<HTMLParagraphElement>("#upMsg");
    const activeBox = qs<HTMLDivElement>("#upActive");

    // --- Stato locale della coda (la verità resta backend-side) ---
    const items: QueueItem[] = [];
    const files = new Map<string, File>(); // uid -> File (solo non-HEIC)
    const thumbs = new Map<string, string>(); // uid -> objectURL
    let activeUid: string | null = null;
    let uploading = false;
    // "Mantieni per tutte le foto": per-campo, default OFF (mai GPS/data/gender).
    const keep: KeepFlags = { ...NO_KEEP };

    // Cleanup thumbnail alla chiusura del foglio.
    uploadCleanup = () => {
      for (const url of thumbs.values()) URL.revokeObjectURL(url);
      thumbs.clear();
    };

    function activeItem(): QueueItem | null {
      return activeUid ? (items.find((i) => i.uid === activeUid) ?? null) : null;
    }

    function editable(it: QueueItem | null): it is QueueItem {
      return !!it && it.status !== "done" && it.status !== "uploading";
    }

    /** Propaga i campi "mantieni per tutte" DALLA foto attiva alle altre. */
    function propagateKept(): void {
      const src = activeItem();
      if (!src) return;
      const kept = keptValues(src, keep);
      if (Object.keys(kept).length === 0) return;
      for (const it of items) {
        if (it.uid !== src.uid) applyKept(it, kept);
      }
    }

    const upCatSelect = wireCategorySelect(qs<HTMLDivElement>("#upCats"), panel, () => {
      const it = activeItem();
      if (!editable(it)) return;
      it.categories = upCatSelect.getSelected();
      if (keep.categories) propagateKept();
    });

    // Picker UNICO legato alla foto attiva: coordinate per-foto, mai bulk. Cattura
    // anche il "Nome luogo" auto-compilato dalla ricerca (che valorizza #upPlace).
    uploadLocationPicker = createLocationPicker(deps.map, panel, placeInput, null, null, (la, lo) => {
      const it = activeItem();
      if (!editable(it)) return;
      it.lat = la;
      it.lng = lo;
      it.placeLabel = placeInput.value.trim() || null;
      if (keep.placeLabel) propagateKept();
      renderQueue();
    });

    qs<HTMLButtonElement>("#upClose").addEventListener("click", closeUpload);

    // --- Editor della foto attiva: ogni campo è PER-FOTO ---
    descInput.addEventListener("input", () => {
      const it = activeItem();
      if (!editable(it)) return;
      it.description = descInput.value.trim() || null;
      if (keep.description) propagateKept();
    });
    placeInput.addEventListener("input", () => {
      const it = activeItem();
      if (!editable(it)) return;
      it.placeLabel = placeInput.value.trim() || null;
      if (keep.placeLabel) propagateKept();
    });
    const privInput = document.querySelector<HTMLInputElement>("#upPrivate");
    privInput?.addEventListener("change", () => {
      const it = activeItem();
      if (!editable(it)) return;
      it.isPrivate = privInput.checked;
      if (keep.privacy) propagateKept();
    });

    // --- "Mantieni per tutte le foto": toggle per-campo (mai GPS/data/gender) ---
    function wireKeep(id: string, field: keyof KeepFlags): void {
      const el = document.querySelector<HTMLInputElement>(id);
      el?.addEventListener("change", () => {
        keep[field] = el.checked;
        if (el.checked) propagateKept(); // applica subito il valore della foto attiva
      });
    }
    wireKeep("#upKeepCats", "categories");
    wireKeep("#upKeepPlace", "placeLabel");
    wireKeep("#upKeepDesc", "description");
    wireKeep("#upKeepPriv", "privacy");

    function statusLabel(it: QueueItem): string {
      switch (it.status) {
        case "uploading":
          return "Caricamento…";
        case "done":
          return "Caricata ✓";
        case "error":
          return it.error ?? "Errore";
        case "unsupported":
          return it.error ?? "Formato non supportato";
        default:
          return canUpload(it) ? "Pronta" : "Manca la posizione";
      }
    }

    // Rail orizzontale: una miniatura per foto, stato a colpo d'occhio, scroll
    // laterale → con molte foto non serve scroll verticale per raggiungerle.
    // Roving tabindex per la navigazione da tastiera (ArrowLeft/Right + Invio).
    function renderQueue(): void {
      const list = qs<HTMLDivElement>("#upQueue");
      if (items.length === 0) {
        list.innerHTML = `<p class="form__hint up-rail__empty">Nessuna foto selezionata.</p>`;
      } else {
        const focusUid = activeUid ?? items[0].uid;
        list.innerHTML = items
          .map((it) => {
            const thumb = thumbs.get(it.uid);
            const sel = it.uid === activeUid;
            const stateCls =
              it.status === "done"
                ? " is-done"
                : it.status === "error" || it.status === "unsupported"
                  ? " is-error"
                  : !it.heic && (it.lat === null || it.lng === null)
                    ? " is-missing"
                    : " is-ready";
            const tab = it.uid === focusUid ? "0" : "-1";
            return `
            <div class="up-rail__item${sel ? " is-active" : ""}${stateCls}" role="option" aria-selected="${sel}" tabindex="${tab}" data-uid="${it.uid}" title="${escapeHtml(it.name)}">
              ${
                thumb
                  ? `<img class="up-rail__thumb" src="${thumb}" alt="" />`
                  : `<span class="up-rail__thumb up-rail__thumb--none">HEIC</span>`
              }
              <span class="up-rail__status">${escapeHtml(statusLabel(it))}</span>
              ${
                it.status !== "uploading"
                  ? `<button type="button" class="up-rail__remove" data-act="remove" aria-label="Rimuovi ${escapeHtml(it.name)}">${icon("close", { size: 14 })}</button>`
                  : ""
              }
            </div>`;
          })
          .join("");
      }
      const s = summarize(items);
      const submitBtn = qs<HTMLButtonElement>("#upSubmit");
      submitBtn.disabled = uploading || s.uploadable === 0;
      submitBtn.innerHTML = `${icon("add", { size: 18 })} ${
        uploading ? "Caricamento…" : s.uploadable > 0 ? `Carica ${s.uploadable} foto` : "Carica"
      }`;
      // Riepilogo testuale nel footer (separato dai messaggi transitori #upMsg).
      const summary = qs<HTMLParagraphElement>("#upSummary");
      if (items.length === 0) {
        summary.textContent = "";
      } else {
        const parts = [`${s.uploadable} pronte`];
        if (s.missingCoords > 0) parts.push(`${s.missingCoords} da completare`);
        if (s.error > 0) parts.push(`${s.error} con errori`);
        if (s.unsupported > 0) parts.push(`${s.unsupported} non supportate`);
        if (s.done > 0) parts.push(`${s.done} caricate`);
        summary.textContent = parts.join(" · ");
      }
    }

    function setActive(uid: string | null): void {
      activeUid = uid;
      const it = uid ? (items.find((i) => i.uid === uid) ?? null) : null;
      if (it && !it.heic) {
        activeBox.hidden = false;
        qs<HTMLElement>("#upActiveName").textContent = it.name;
        uploadLocationPicker?.loadCoords(it.lat, it.lng);
        dateInput.value = it.takenAt ?? "";
        descInput.value = it.description ?? "";
        placeInput.value = it.placeLabel ?? "";
        upCatSelect.setSelected(it.categories);
        if (privInput) privInput.checked = it.isPrivate;
      } else {
        activeBox.hidden = true;
        uploadLocationPicker?.loadCoords(null, null);
      }
      renderQueue();
    }

    async function readImageSize(file: File): Promise<{ width: number; height: number } | null> {
      try {
        const bmp = await createImageBitmap(file);
        const out = { width: bmp.width, height: bmp.height };
        bmp.close();
        return out;
      } catch {
        return null;
      }
    }

    // Legge dimensioni (natural, NON da EXIF) + EXIF (solo GPS/data) per-foto.
    async function hydrate(it: QueueItem): Promise<void> {
      const f = files.get(it.uid);
      if (!f) return;
      const size = await readImageSize(f);
      if (size) {
        it.width = size.width;
        it.height = size.height;
      }
      try {
        const { readExif } = await import("../exif");
        const exif = await readExif(f);
        if (exif.latitude !== undefined && exif.longitude !== undefined) {
          it.lat = exif.latitude;
          it.lng = exif.longitude;
          it.exifApplied = true;
          if (it.uid === activeUid) uploadLocationPicker?.loadCoords(it.lat, it.lng);
        }
        if (exif.takenAt) {
          it.takenAt = toDatetimeLocal(exif.takenAt);
          it.exifApplied = true;
          if (it.uid === activeUid) dateInput.value = it.takenAt;
        }
      } catch {
        /* EXIF assente/illeggibile: si ignora (resta inserimento manuale) */
      }
      renderQueue();
    }

    function removeItem(uid: string): void {
      const idx = items.findIndex((i) => i.uid === uid);
      if (idx < 0) return;
      const t = thumbs.get(uid);
      if (t) URL.revokeObjectURL(t);
      thumbs.delete(uid);
      files.delete(uid);
      items.splice(idx, 1);
      if (activeUid === uid) {
        const next = items.find((i) => !i.heic && i.status !== "done");
        setActive(next ? next.uid : null);
      } else {
        renderQueue();
      }
    }

    fileInput.addEventListener("change", () => {
      const picked = Array.from(fileInput.files ?? []);
      fileInput.value = ""; // consente di riselezionare; la coda tiene i file
      if (picked.length === 0) return;
      const slots = remainingSlots(items.length);
      const toAdd = picked.slice(0, slots);
      const overflow = picked.length - toAdd.length;
      const fresh: QueueItem[] = [];
      for (const f of toAdd) {
        const uid = crypto.randomUUID();
        const heic = isHeic(f);
        const it = createQueueItem(uid, f.name, heic);
        items.push(it);
        fresh.push(it);
        if (!heic) {
          files.set(uid, f);
          thumbs.set(uid, URL.createObjectURL(f));
          if (maxBytes && f.size > maxBytes) {
            it.status = "error";
            it.error = `File troppo grande (max ${formatBytes(maxBytes)}).`;
          }
        }
      }
      msg.textContent = overflow > 0 ? `Massimo ${MAX_BATCH} foto per batch: ${overflow} ignorate.` : "";
      // Le nuove foto ereditano i campi con "Mantieni per tutte" attivo, dalla
      // foto attiva corrente (mai GPS/data/gender). Nessuna sorgente al 1° batch.
      const keepSrc = activeItem();
      if (keepSrc) {
        const kept = keptValues(keepSrc, keep);
        if (Object.keys(kept).length > 0) for (const it of fresh) applyKept(it, kept);
      }
      if (!activeUid) {
        const firstEditable = items.find((i) => !i.heic && i.status !== "done");
        if (firstEditable) setActive(firstEditable.uid);
      }
      renderQueue();
      for (const it of fresh) {
        if (!it.heic && it.status !== "error") void hydrate(it);
      }
    });

    dateInput.addEventListener("change", () => {
      if (!activeUid) return;
      const it = items.find((i) => i.uid === activeUid);
      if (it && it.status !== "done" && it.status !== "uploading") it.takenAt = dateInput.value || null;
    });

    const railEl = qs<HTMLDivElement>("#upQueue");
    railEl.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const row = target.closest<HTMLElement>(".up-rail__item");
      if (!row) return;
      const uid = row.dataset.uid;
      if (!uid) return;
      // Rimozione tramite la X; tap sulla miniatura = selezione foto attiva.
      // (Le foto in errore si ri-caricano semplicemente ripremendo "Carica".)
      if (target.closest<HTMLButtonElement>("button[data-act='remove']")) removeItem(uid);
      else setActive(uid);
    });
    // Navigazione da tastiera della rail (roving tabindex).
    railEl.addEventListener("keydown", (e) => {
      const cur = (e.target as HTMLElement).closest<HTMLElement>(".up-rail__item");
      if (!cur) return;
      if (e.key === "Enter" || e.key === " ") {
        if (cur.dataset.uid) {
          e.preventDefault();
          setActive(cur.dataset.uid);
        }
      } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        const all = [...railEl.querySelectorAll<HTMLElement>(".up-rail__item")];
        const next = all[all.indexOf(cur) + (e.key === "ArrowRight" ? 1 : -1)];
        if (next) {
          cur.tabIndex = -1;
          next.tabIndex = 0;
          next.focus();
        }
      }
    });

    async function startUpload(): Promise<void> {
      if (uploading) return;
      const pre = summarize(items);
      if (pre.uploadable === 0) {
        msg.textContent =
          pre.missingCoords > 0
            ? "Imposta la posizione delle foto evidenziate prima di caricare."
            : "Niente da caricare.";
        return;
      }
      uploading = true;
      renderQueue();
      msg.textContent = "Caricamento in corso…";

      // Assicura che i campi "Mantieni per tutte" attivi siano allineati prima
      // dell'invio (la propagazione è già live, questo è un ultimo sync difensivo).
      propagateKept();
      let okCount = 0;
      let stopped = false;
      let lastStorageState: StorageState | null = null;

      // Sequenziale (concorrenza 1): la quota viene riletta dal backend a ogni
      // POST → nessuna race; gli errori sono per-foto e non bloccano la coda.
      for (const it of items) {
        if (!canUpload(it)) continue;
        const fields = uploadFieldsFor(it);
        const f = files.get(it.uid);
        if (!fields || !f) continue;
        it.status = "uploading";
        it.error = null;
        renderQueue();

        const fd = new FormData();
        fd.set("file", f);
        for (const [k, v] of Object.entries(fields)) fd.set(k, v);

        try {
          const res = await uploadPhoto(fd);
          it.status = "done";
          okCount++;
          // Inserimento ottimistico (riconciliato da refresh() a fine batch).
          deps.prependPhoto(uploadToSummary(res));
          lastStorageState = res.storage.state;
        } catch (err) {
          it.status = "error";
          if (err instanceof ApiError && err.status === 429) {
            // Rate limit volumetrico: gli upload successivi sarebbero anch'essi
            // bloccati → fermo la coda, le restanti restano pending/riprovabili.
            const secs = err.retryAfterSeconds;
            it.error = "Troppi caricamenti ravvicinati.";
            msg.textContent =
              secs != null
                ? `Limite di caricamenti raggiunto. Riprova tra ~${Math.max(1, Math.ceil(secs / 60))} min.`
                : "Limite di caricamenti raggiunto. Attendi qualche minuto e riprova.";
            stopped = true;
            renderQueue();
            break;
          }
          it.error = uploadErrorMessage(err);
        }
        renderQueue();
      }

      uploading = false;
      deps.render();
      await deps.refresh();
      renderQueue();

      if (lastStorageState === "warning") deps.setStatus("Foto caricate. Attenzione: spazio quasi esaurito.");
      else if (lastStorageState === "full") deps.setStatus("Foto caricate. Spazio ora esaurito.");
      else if (okCount > 0) deps.setStatus(`${okCount} ${okCount === 1 ? "foto caricata" : "foto caricate"}.`);

      const post = summarize(items);
      if (!stopped && post.total > 0 && post.total === post.done) {
        closeUpload();
        return;
      }
      if (!stopped) {
        const errPart = post.error > 0 ? ` ${post.error} con errori.` : "";
        const missPart = post.missingCoords > 0 ? ` ${post.missingCoords} senza posizione.` : "";
        msg.textContent = okCount > 0 ? `${okCount} caricate.${errPart}${missPart}`.trim() : msg.textContent;
      }
    }

    qs<HTMLFormElement>("#upForm").addEventListener("submit", (e) => {
      e.preventDefault();
      void startUpload();
    });

    renderQueue();
  }

  return {
    open: openUpload,
    close: closeUpload,
    isOpen: () => !qs<HTMLDivElement>("#upload").hidden,
  };
}
